"use client";

// Google Tag Manager for the public website only (home, blog, guides, legal pages) –
// it is rendered from SiteFooter, which the app, admin panel and quote links don't use.
//
// Consent first: Google Consent Mode v2 starts with everything denied, so tags run
// without cookies until the visitor accepts in the banner. The choice is remembered
// in this browser and can be changed from "Cookie settings" in the footer.

import { useEffect, useState } from "react";
import { BP } from "./site";

export const GTM_ID = process.env.NEXT_PUBLIC_GTM_ID ?? "GTM-5MMZHSJK";
const CONSENT_KEY = "i24-cookie-consent"; // "granted" | "denied"
export const GTM_FLAG = "__i24Gtm"; // set on window once the container has loaded

const readChoice = () => {
  try {
    return localStorage.getItem(CONSENT_KEY) || "";
  } catch {
    return "";
  }
};
const saveChoice = (v) => {
  try {
    localStorage.setItem(CONSENT_KEY, v);
  } catch {
    /* private mode – the banner simply asks again next time */
  }
};

function gtag() {
  window.dataLayer.push(arguments); // gtag() must push the arguments object, not an array
}

const consent = (granted) => {
  const v = granted ? "granted" : "denied";
  return { analytics_storage: v, ad_storage: v, ad_user_data: v, ad_personalization: v };
};

function loadGtm(choice) {
  if (!GTM_ID || window[GTM_FLAG]) return;
  window[GTM_FLAG] = true;
  window.dataLayer = window.dataLayer || [];
  gtag("consent", "default", { ...consent(false), wait_for_update: 500 });
  if (choice === "granted") gtag("consent", "update", consent(true));
  window.dataLayer.push({ "gtm.start": Date.now(), event: "gtm.js" });
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(GTM_ID)}`;
  document.head.appendChild(s);
}

const OPEN_EVENT = "i24-cookie-settings";
export const openCookieSettings = () => window.dispatchEvent(new Event(OPEN_EVENT));

export default function Analytics() {
  const [show, setShow] = useState(false);

  useEffect(() => {
    const choice = readChoice();
    loadGtm(choice);
    setShow(!choice);
    const open = () => setShow(true);
    window.addEventListener(OPEN_EVENT, open);
    return () => window.removeEventListener(OPEN_EVENT, open);
  }, []);

  const decide = (granted) => {
    saveChoice(granted ? "granted" : "denied");
    if (window.dataLayer) gtag("consent", "update", consent(granted));
    setShow(false);
  };

  return (
    <>
      {GTM_ID && (
        <noscript>
          <iframe src={`https://www.googletagmanager.com/ns.html?id=${GTM_ID}`} height="0" width="0" style={{ display: "none", visibility: "hidden" }} title="Google Tag Manager" />
        </noscript>
      )}
      {show && (
        <div role="dialog" aria-label="Cookie choices" className="fixed inset-x-3 bottom-3 z-50 mx-auto max-w-xl rounded-2xl bg-white p-5 text-sm text-slate-700 shadow-pop ring-1 ring-slate-900/10 sm:inset-x-auto sm:right-5 sm:bottom-5" data-testid="cookie-banner">
          <div className="font-semibold text-slate-900">Cookies on invoice24.in</div>
          <p className="mt-1.5 leading-relaxed">
            We’d like to use Google analytics cookies to see which pages help people, so we can improve the site. They’re off until you accept. The app itself never uses them, and your invoices are never shared.{" "}
            <a href={`${BP}/privacy/#cookies-and-browser-storage`} className="font-medium text-brand-700 underline">
              Privacy policy
            </a>
          </p>
          <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:justify-end">
            <button type="button" onClick={() => decide(false)} className="rounded-lg px-4 py-2 font-semibold text-slate-700 ring-1 ring-slate-300 hover:bg-slate-50" data-testid="cookie-reject">
              Reject
            </button>
            <button type="button" onClick={() => decide(true)} className="rounded-lg bg-slate-900 px-4 py-2 font-semibold text-white hover:bg-slate-800" data-testid="cookie-accept">
              Accept analytics
            </button>
          </div>
        </div>
      )}
    </>
  );
}

// A link into the app (/invoice/) as a full page load, so nothing loaded on the public
// site – Tag Manager included – carries over into the app.
export function AppLink({ href, children, ...props }) {
  return (
    <a href={`${BP}${href}`} {...props}>
      {children}
    </a>
  );
}

// Footer button that reopens the banner.
export function CookieSettingsButton({ className }) {
  return (
    <button type="button" onClick={openCookieSettings} className={className}>
      Cookie settings
    </button>
  );
}

// For the app, admin panel and quote links: if a soft navigation brought Tag Manager
// along from the public site, reload once so the page runs without it.
export function useNoTagManager() {
  useEffect(() => {
    if (window[GTM_FLAG]) window.location.reload();
  }, []);
}
